/// Desktop: remappable jump / duck keys. Click a card, then press a key.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultDuckKey, defaultJumpKey, formatKeyLabel, isBindableKey } from '@/settings/data/keyBindings';
import { useSettingsStore, useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';

type CaptureTarget = 'jump' | 'duck' | null;

export function KeyboardBindingsEditor() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const jumpKey = useSettingsStore((s) => s.jumpKey);
  const duckKey = useSettingsStore((s) => s.duckKey);
  const setJumpKey = useSettingsStore((s) => s.setJumpKey);
  const setDuckKey = useSettingsStore((s) => s.setDuckKey);
  const resetKeyboardBindings = useSettingsStore((s) => s.resetKeyboardBindings);

  const [capturing, setCapturing] = useState<CaptureTarget>(null);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!capturing) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Escape') {
        setCapturing(null);
        setConflict(false);
        return;
      }
      if (!isBindableKey(event.key)) return;
      const ok = capturing === 'jump' ? setJumpKey(event.key) : setDuckKey(event.key);
      if (!ok) {
        setConflict(true);
        return;
      }
      setConflict(false);
      setCapturing(null);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capturing, setDuckKey, setJumpKey]);

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex gap-3">
        <BindCard
          action={t('controlsDuck')}
          icon="arrow_down"
          detail={capturing === 'duck' ? t('controlsPressKey') : formatKeyLabel(duckKey)}
          listening={capturing === 'duck'}
          onClick={() => {
            setConflict(false);
            setCapturing((current) => (current === 'duck' ? null : 'duck'));
          }}
        />
        <BindCard
          action={t('controlsJump')}
          icon="arrow_up"
          detail={capturing === 'jump' ? t('controlsPressKey') : formatKeyLabel(jumpKey)}
          listening={capturing === 'jump'}
          onClick={() => {
            setConflict(false);
            setCapturing((current) => (current === 'jump' ? null : 'jump'));
          }}
        />
      </div>
      {conflict && (
        <p className="text-center leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
          {t('controlsKeyInUse')}
        </p>
      )}
      <p className="text-center leading-tight" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.75 }}>
        {t('controlsKeyboardHint')}
      </p>
      {!(jumpKey === defaultJumpKey && duckKey === defaultDuckKey) && (
        <button
          type="button"
          onClick={() => {
            resetKeyboardBindings();
            setCapturing(null);
            setConflict(false);
          }}
          className="self-center leading-none"
          style={{ fontSize: 16, color: colors.onSurface, opacity: 0.55 }}
        >
          {t('controlsResetDefaults')}
        </button>
      )}
    </div>
  );
}

function BindCard({
  action,
  icon,
  detail,
  listening,
  onClick,
}: {
  action: string;
  icon: string;
  detail: string;
  listening: boolean;
  onClick: () => void;
}) {
  const colors = useThemeColors();
  const border = listening ? '#FFC107' : colors.onSurface;

  return (
    <div className="min-w-0 flex-1">
      <PixelContainer
        radius={2}
        pixelScale={2}
        padding="14px 10px"
        backgroundColor={colors.surfaceContainerHigh}
        borderColor={border}
        className="w-full"
        onClick={onClick}
      >
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-center justify-center gap-1.5">
            <PixelIcon name={icon} size={18} color={listening ? '#FFC107' : colors.onSurface} />
            <span className="truncate leading-none" style={{ fontSize: 20, color: listening ? '#FFC107' : colors.onSurface }}>
              {action}
            </span>
          </div>
          <span
            className="text-center leading-tight"
            style={{ fontSize: 14, color: listening ? '#FFC107' : colors.onSurface, opacity: listening ? 1 : 0.65 }}
          >
            {detail}
          </span>
        </div>
      </PixelContainer>
    </div>
  );
}
